import { HD_ORCHIDS_BUSINESS_MODES, normalizeBusinessMode } from '../config/moduleDefinitions';

const cleanRole = (value) => String(value || '')
  .trim()
  .toLowerCase()
  .replace(/[^a-z0-9]+/g, '_')
  .replace(/^_+|_+$/g, '');

const normalizePermissions = (permissions = []) => Array.from(new Set((Array.isArray(permissions) ? permissions : []).map((value) => String(value || '').trim()).filter(Boolean)));

const COLD_OPERATIONAL_PREFIXES = Object.freeze([
  'dashboard_', 'agreement', 'grower', 'subgrower', 'inward', 'gate_', 'gatepass',
  'weigh', 'unloading', 'inspection', 'quality_', 'lot_', 'storage_', 'chamber_',
  'bin_', 'crate_', 'grading_', 'packing_', 'retrieval', 'outward', 'dispatch',
  'warehouse_', 'stock_', 'cold_', 'temperature_', 'finance_', 'billing_',
]);

const isColdOperationalPermission = (permission = '') => {
  const code = String(permission || '').trim().toLowerCase();
  if (!code || code === 'attendance_selfie' || code.startsWith('hd_orchids_')) return false;
  return COLD_OPERATIONAL_PREFIXES.some((prefix) => code.startsWith(prefix));
};

const normalizeScope = (value = '') => {
  const clean = String(value || '').trim().toLowerCase().replace(/[\s-]+/g, '_');
  if (['both','mixed','all'].includes(clean)) return HD_ORCHIDS_BUSINESS_MODES.BOTH;
  if (['hd_orchids','hd_orchid','orchard','orchards','orchid','orchids'].includes(clean)) return HD_ORCHIDS_BUSINESS_MODES.HD_ORCHIDS;
  if (['cold_storage','cold','storage','warehouse'].includes(clean)) return HD_ORCHIDS_BUSINESS_MODES.COLD_STORAGE;
  return '';
};

export const isCompanyAdminPrincipal = (userData = {}, permissions = []) => {
  const rows = normalizePermissions(permissions);
  if (rows.includes('all') || rows.includes('*')) return true;
  if (userData?.metadata?.isCompanyAdmin === true) return true;
  const candidates = [
    userData?.roleKey,
    userData?.roleCode,
    userData?.roleName,
    userData?.role?.key,
    userData?.role?.code,
    userData?.role?.name,
  ].map(cleanRole).filter(Boolean);
  return candidates.some((role) => ['company_admin','company_owner','owner','admin'].includes(role));
};

export const principalBusinessScope = ({ userData = {}, roleData = null, permissions = [], companyMode = '' } = {}) => {
  const explicit = normalizeScope(
    roleData?.businessScope || roleData?.businessUnit || roleData?.workspaceScope ||
    userData?.role?.businessScope || userData?.businessScope || userData?.workspaceScope || ''
  );
  const mode = companyMode ? normalizeBusinessMode(companyMode) : '';
  if (mode && mode !== HD_ORCHIDS_BUSINESS_MODES.BOTH) return mode;
  if (explicit) return explicit;
  const rows = normalizePermissions(permissions);
  if (rows.includes('all') || rows.includes('*')) return HD_ORCHIDS_BUSINESS_MODES.BOTH;
  const hd = rows.some((code) => code.startsWith('hd_orchids_'));
  const cold = rows.some(isColdOperationalPermission);
  if (hd && cold) return HD_ORCHIDS_BUSINESS_MODES.BOTH;
  if (hd) return HD_ORCHIDS_BUSINESS_MODES.HD_ORCHIDS;
  if (cold) return HD_ORCHIDS_BUSINESS_MODES.COLD_STORAGE;
  return HD_ORCHIDS_BUSINESS_MODES.COLD_STORAGE;
};

export const workspaceAccessForPrincipal = ({ userData = {}, roleData = null, permissions = [], isSuperAdmin = false, companyMode = '' } = {}) => {
  const rows = normalizePermissions(permissions);
  const privileged = isSuperAdmin || isCompanyAdminPrincipal(userData, rows);
  let coldStorage = privileged || rows.some(isColdOperationalPermission);
  let hdOrchids = privileged || rows.some((code) => code.startsWith('hd_orchids_'));

  const scope = principalBusinessScope({ userData, roleData, permissions: rows, companyMode });
  if (scope === HD_ORCHIDS_BUSINESS_MODES.COLD_STORAGE) hdOrchids = false;
  if (scope === HD_ORCHIDS_BUSINESS_MODES.HD_ORCHIDS) coldStorage = false;

  const mode = companyMode ? normalizeBusinessMode(companyMode) : '';
  if (mode === HD_ORCHIDS_BUSINESS_MODES.COLD_STORAGE) hdOrchids = false;
  if (mode === HD_ORCHIDS_BUSINESS_MODES.HD_ORCHIDS) coldStorage = false;

  return { coldStorage, hdOrchids, businessScope: scope };
};

export const dashboardAccessForPrincipal = ({ userData = {}, roleData = null, permissions = [], isSuperAdmin = false, companyMode = '' } = {}) => {
  const rows = normalizePermissions(permissions);
  const workspace = workspaceAccessForPrincipal({ userData, roleData, permissions: rows, isSuperAdmin, companyMode });
  const privileged = isSuperAdmin || isCompanyAdminPrincipal(userData, rows);
  return {
    ...workspace,
    coldDashboard: workspace.coldStorage && (privileged || rows.includes('dashboard_view')),
    hdDashboard: workspace.hdOrchids && (privileged || rows.includes('hd_orchids_dashboard_view')),
  };
};

export const canHd = ({ userData = {}, permissions = [], hasPermission, permission }) => {
  if (isCompanyAdminPrincipal(userData, permissions)) return true;
  return permission ? Boolean(hasPermission?.(permission)) : false;
};
